/**
 * src/lib/services/bookings.ts
 * Booking and workflow service stubs for Phase 3 shell.
 * Phase 4: swap mock returns for fetch() calls to the API.
 */

import {
  MOCK_AUDIT,
  getActiveBooking,
} from '@/lib/mock/bookings';
import type { BookingJob, BackendWorkflow } from '@/lib/types/workflow';
import { ACTIVE_WORKFLOW_STATUSES } from '@/lib/types/workflow';
import type {
  BookingJob as BackendJob,
  ApiError as IApiError,
  ApproveWorkflowPayload,
  RejectWorkflowPayload,
  RawEmail,
  Draft,
  UpdateDraftPayload,
  ApproveDraftPayload,
  PricingLineItem,
  PriceTier,
} from '@/lib/types/booking';
import type { AuditEntry } from '@/lib/types/audit';
import { get, post, patch, postKeepalive } from '@/lib/api/client';

export type AiUsageData = {
  draft_id: string;
  workflow_id: string;
  model_name: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  estimated_cost_zar: string | null;
  duration_ms: number | null;
  status: string | null;
  recorded_at: string | null;
};

// ---- Workflow queries ----------------------------------------------------

export interface WorkflowListFilters {
  search?: string;
  date_from?: string;
  date_to?: string;
}

function buildWorkflowParams(filters?: WorkflowListFilters, base?: Record<string, string>): URLSearchParams {
  const params = new URLSearchParams(base);
  if (filters?.search) params.set('search', filters.search);
  if (filters?.date_from) params.set('date_from', filters.date_from);
  if (filters?.date_to) params.set('date_to', filters.date_to);
  return params;
}

export async function getWorkflows(filters?: WorkflowListFilters): Promise<BackendJob[]> {
  try {
    const qs = buildWorkflowParams(filters).toString();
    return await get<BackendJob[]>(`/api/v1/workflows${qs ? `?${qs}` : ''}`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

// Statuses that belong in the Dashboard's active Inbound Queue — the
// complement of useBookingQueue.ts's TERMINAL_QUEUE_STATUSES.
//
// Sourced from the single shared ACTIVE_WORKFLOW_STATUSES constant (see
// its doc comment in workflow.ts) rather than a locally-hardcoded list —
// this used to be its own three-status list (RECEIVED/PROCESSING/
// PENDING_APPROVAL only), which silently fell out of sync with
// DashboardPage.tsx's INBOX_QUEUE_STATUSES and meant an
// APPROVED/RESERVATION_CONFIRMED/DRAFTED/FAILED workflow was never even
// fetched from the backend, regardless of the frontend already being
// built to display it.
const ACTIVE_QUEUE_STATUSES = ACTIVE_WORKFLOW_STATUSES;

/**
 * GET /api/v1/workflows is paginated (default limit 20) and unfiltered by
 * status when called via plain getWorkflows() above — so once enough
 * newer bookings of ANY status (including terminal ones like SENT) exist,
 * an older still-active booking silently falls off the default page and
 * never appears in the operator's queue. This isn't reingest-specific:
 * any RECEIVED/PROCESSING/PENDING_APPROVAL booking old enough relative to
 * total volume has the same problem, since getWorkflows() never asked for
 * "active bookings" — only "whatever was created most recently."
 *
 * Fixes the reported symptom where a re-ingested REJECTED booking (whose
 * created_at is its original, months-old date — only updated_at reflects
 * the reingest) never showed up on the Dashboard despite genuinely
 * transitioning to RECEIVED server-side (confirmed via the raw
 * POST .../reingest/user response and a direct DB check — the backend
 * transition was correct all along).
 *
 * Fetches each active status explicitly in parallel with a high limit,
 * mirroring getRejectedArchive's REJECTED+QUARANTINED merge below rather
 * than relying on the default unfiltered page.
 */
export async function getActiveWorkflows(filters?: WorkflowListFilters): Promise<BackendJob[]> {
  try {
    const unwrap = (data: unknown): BackendJob[] =>
      Array.isArray(data) ? data : ((data as { items?: BackendJob[] })?.items ?? []);

    const results = await Promise.all(
      ACTIVE_QUEUE_STATUSES.map((status) => {
        const qs = buildWorkflowParams(filters, { status, limit: '100' }).toString();
        return get<BackendJob[]>(`/api/v1/workflows?${qs}`).then(unwrap);
      }),
    );

    return results.flat();
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export async function getWorkflow(id: string): Promise<BackendJob> {
  try {
    return await get<BackendJob>(`/api/v1/workflows/${id}`);
  } catch (err: unknown) {
    if (isApiError(err) && err.status === 404) {
      throw { status: 404, message: `Workflow ${id} not found` } as IApiError;
    }
    throw normaliseError(err);
  }
}

export async function claimWorkflow(workflowId: string): Promise<BackendJob> {
  try {
    return await post<BackendJob>(`/api/v1/workflows/${workflowId}/claim`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export async function approveWorkflow(
  id: string,
  payload: ApproveWorkflowPayload,
): Promise<BackendJob> {
  try {
    return await post<BackendJob>(`/api/v1/workflows/${id}/approve`, {
      actor_id: payload.actor_id,
      reason: payload.reason ?? '',
    });
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

/**
 * Sends the quote email for an already-approved workflow and transitions
 * it APPROVED -> RESERVATION_CONFIRMED -> SENT. Call only after the
 * operator has confirmed stock holds/substitutions in the
 * STOCK_RESERVATION step (Finding 1/3) — approveWorkflow() above never
 * sends anything on its own.
 */
export async function finalizeSendWorkflow(
  id: string,
  payload: ApproveWorkflowPayload,
): Promise<BackendJob> {
  try {
    return await post<BackendJob>(`/api/v1/workflows/${id}/finalize-send`, {
      actor_id: payload.actor_id,
      reason: payload.reason ?? '',
    });
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export async function rejectWorkflow(
  id: string,
  payload: RejectWorkflowPayload,
): Promise<BackendJob> {
  try {
    return await post<BackendJob>(`/api/v1/workflows/${id}/reject`, {
      actor_id: payload.actor_id,
      reason: payload.reason ?? '',
    });
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

/**
 * Reverts an APPROVED workflow back to PENDING_APPROVAL. Used when the
 * operator cancels an in-progress stock reservation
 * (StockReservationPanel's "Cancel Reservation") — without this call, the
 * frontend reset its own local screen to "Pending Review" but the backend
 * workflow stayed APPROVED, which caused two live-reported bugs: the
 * dashboard queue kept showing "Approved" for a booking the operator had
 * just backed out of, and a subsequent Reject attempt 422'd (reject_workflow
 * guards status == PENDING_APPROVAL). Calls the existing generic
 * /transition endpoint rather than a new one — APPROVED -> PENDING_APPROVAL
 * was added to workflow-service's VALID_TRANSITIONS specifically for this.
 * See [[finding-cancel-reservation-does-not-revert-workflow-status]].
 */
export async function revertToPendingApproval(
  id: string,
  payload: ApproveWorkflowPayload,
): Promise<BackendJob> {
  try {
    return await post<BackendJob>(`/api/v1/workflows/${id}/transition`, {
      new_status: 'PENDING_APPROVAL',
      actor_id: payload.actor_id,
      reason: payload.reason ?? 'Operator cancelled the stock reservation',
    });
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export async function getEmail(id: string): Promise<RawEmail> {
  try {
    return await get<RawEmail>(`/api/v1/emails/${id}`);
  } catch (err: unknown) {
    if (isApiError(err) && err.status === 404) {
      throw { status: 404, message: `Email ${id} not found` } as IApiError;
    }
    throw normaliseError(err);
  }
}

/**
 * Client replies that arrived after their workflow reached APPROVED/SENT —
 * blocked from re-entering the pipeline as a new booking by email-service's
 * thread-isolation check, but still stored and worth an operator seeing.
 * See [[finding-reply-after-send-not-blocked]].
 */
export async function getThreadReplies(): Promise<RawEmail[]> {
  try {
    return await get<RawEmail[]>('/api/v1/emails/thread-replies');
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export interface SendReplyPayload {
  actor_id: string;
  body_text: string;
  body_html?: string;
}

export interface SendReplyResult {
  status: string;
  message_id: string;
}

/**
 * Send a direct, operator-typed reply to a client's thread-reply email
 * (Archive → Replies tab). Handled by workflow-service, not email-service —
 * it owns outbound SES sending (app.services.ses_client) so the reply gets
 * proper In-Reply-To/References threading headers back to the client's
 * message. No approval step; this is correspondence, not a new booking.
 */
export async function sendReply(
  rawEmailId: string,
  payload: SendReplyPayload,
): Promise<SendReplyResult> {
  try {
    return await post<SendReplyResult>(`/api/v1/emails/${rawEmailId}/reply`, payload);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export interface ReplyDraftSuggestion {
  suggested_body_text: string;
  suggested_body_html: string | null;
  // Null whenever the reply didn't request a repricing-relevant change, or
  // the re-pricing pipeline failed for any reason — the text suggestion
  // above still generates either way. See
  // [[feature-scope-ai-priced-reply-quote]].
  total_price: number | null;
  price_currency: string | null;
  pricing_line_items: PricingLineItem[] | null;
  price_tier: PriceTier;
}

/**
 * Ask the AI to suggest a reply to a thread-reply email (Archive → Replies
 * → "Generate AI Draft") — most commonly a client asking to update a
 * quote. Never auto-sent: the caller drops this into the reply composer
 * for the operator to review/edit, then still calls sendReply() above
 * themselves. See [[feature-scope-ai-draft-thread-replies]].
 *
 * Can 404 (no original booking found for this thread) or 422 (email isn't
 * a recognised thread-reply) — callers should surface those distinctly
 * from a generic failure rather than treating every error the same way.
 */
export async function getReplyDraftSuggestion(rawEmailId: string): Promise<ReplyDraftSuggestion> {
  try {
    return await get<ReplyDraftSuggestion>(`/api/v1/emails/${rawEmailId}/reply/draft-suggestion`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export async function getEmailDrafts(id: string): Promise<Draft[]> {
  try {
    return await get<Draft[]>(`/api/v1/emails/${id}/drafts`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export async function updateDraft(
  id: string,
  payload: UpdateDraftPayload,
): Promise<Draft> {
  try {
    return await patch<Draft>(`/api/v1/drafts/${id}`, payload);
  } catch (err: unknown) {
    if (isApiError(err) && err.status === 404) {
      throw { status: 404, message: 'Draft not found' } as IApiError;
    }
    throw normaliseError(err);
  }
}

/**
 * Operator-confirmed CPT/CPT-Local price tier switch on an existing draft
 * (see [[finding-cpt-jhb-tier-pricing-doc-gap]]). Only valid for a Cape Town
 * booking that isn't yet approved — the backend rejects anything else with
 * a 400. Re-prices and regenerates the draft body server-side; the caller
 * should replace its local draft state with the returned Draft.
 */
export async function repriceDraft(
  id: string,
  priceTierOverride: 'CPT' | 'CPT Local',
): Promise<Draft> {
  try {
    return await post<Draft>(`/api/v1/drafts/${id}/reprice`, {
      price_tier_override: priceTierOverride,
    });
  } catch (err: unknown) {
    if (isApiError(err) && err.status === 400) {
      throw { status: 400, message: err.message } as IApiError;
    }
    if (isApiError(err) && err.status === 404) {
      throw { status: 404, message: 'Draft not found' } as IApiError;
    }
    throw normaliseError(err);
  }
}

/**
 * Re-narrates the draft to match gear actually resolved during Stock
 * Reservation (a HELD line, an accepted AI substitute, or an
 * operator-entered manual substitute — see [[finding-manual-substitute-entry]]).
 * Call after a substitution decision (confirmSubstitution) or after the
 * hold is confirmed, passing every current line from the reservation so
 * the draft's body/pricing/gear list reflect what's actually being held,
 * not what was originally requested. The caller should replace its local
 * draft/booking.gear state with the returned Draft — see
 * [[finding-finalize-draft-edit-discarded-on-send]] for the related history
 * of the Finalize screen not reflecting reservation-stage changes.
 */
export async function syncDraftWithReservation(
  id: string,
  lines: { name: string; quantity: number; unitPrice: number }[],
  dates?: { start: string; end: string },
): Promise<Draft> {
  try {
    return await post<Draft>(`/api/v1/drafts/${id}/sync-with-reservation`, {
      lines: lines.map((l) => ({ name: l.name, quantity: l.quantity, unit_price: l.unitPrice })),
      // N3: the rental period the operator confirmed in Stock Check, so the
      // quote is priced per day over the right number of (inclusive) days.
      ...(dates ? { start_date: dates.start, end_date: dates.end } : {}),
    });
  } catch (err: unknown) {
    if (isApiError(err) && err.status === 400) {
      throw { status: 400, message: err.message } as IApiError;
    }
    if (isApiError(err) && err.status === 404) {
      throw { status: 404, message: 'Draft not found' } as IApiError;
    }
    throw normaliseError(err);
  }
}

/**
 * Same request as syncDraftWithReservation, but fired with keepalive so it
 * still reaches the server when the operator closes the tab / navigates away
 * while a debounced sync is waiting to be sent. Fire-and-forget: the
 * response (the regenerated draft) is picked up the next time the draft is
 * loaded.
 */
export function syncDraftWithReservationOnUnload(
  id: string,
  lines: { name: string; quantity: number; unitPrice: number }[],
  dates?: { start: string; end: string },
): void {
  postKeepalive(`/api/v1/drafts/${id}/sync-with-reservation`, {
    lines: lines.map((l) => ({ name: l.name, quantity: l.quantity, unit_price: l.unitPrice })),
    ...(dates ? { start_date: dates.start, end_date: dates.end } : {}),
  });
}

/**
 * N3: operator/automatic retry of the pricing step after the first attempt
 * failed (draft-service POST /drafts/{id}/retry-pricing). Resolves with the
 * freshly priced draft, or rejects with a clear message (502 when pricing is
 * still unavailable) — in which case the quote is NOT priced and the backend
 * will keep refusing to approve/send it.
 */
export async function retryDraftPricing(id: string): Promise<Draft> {
  try {
    return await post<Draft>(`/api/v1/drafts/${id}/retry-pricing`, {});
  } catch (err: unknown) {
    if (isApiError(err) && (err.status === 400 || err.status === 502)) {
      throw { status: err.status, message: err.message } as IApiError;
    }
    if (isApiError(err) && err.status === 404) {
      throw { status: 404, message: 'Draft not found' } as IApiError;
    }
    throw normaliseError(err);
  }
}

export async function approveDraft(
  id: string,
  payload: ApproveDraftPayload,
): Promise<Draft> {
  try {
    return await post<Draft>(`/api/v1/drafts/${id}/approve`, {
      approved_by: payload.approved_by,
    });
  } catch (err: unknown) {
    if (isApiError(err) && err.status === 404) {
      throw { status: 404, message: 'Draft not found' } as IApiError;
    }
    if (isApiError(err) && err.status === 400) {
      throw { status: 400, message: err.message } as IApiError;
    }
    throw normaliseError(err);
  }
}

// ---- Archive queries -----------------------------------------------------

export async function getSentArchive(filters?: WorkflowListFilters): Promise<BackendWorkflow[]> {
  try {
    const qs = buildWorkflowParams(filters, { status: 'SENT' }).toString();
    return await get<BackendWorkflow[]>(`/api/v1/workflows?${qs}`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

/**
 * Archive's "Deleted" tab shows rejected workflows. This used to call
 * draft-service's /deleted-drafts — a soft-deleted-draft concept that has
 * no connection to rejecting a workflow (reject_workflow() in
 * workflow-service only flips booking_workflows.status to REJECTED; it
 * never touches the drafts table). That meant every rejection was
 * invisible here even though it appeared correctly in the Audit Log.
 * Sourcing from workflows with status=REJECTED (same pattern as
 * getSentArchive/getQuarantinedWorkflows) fixes that.
 */
export async function getDeletedArchive(filters?: WorkflowListFilters): Promise<BackendWorkflow[]> {
  try {
    const qs = buildWorkflowParams(filters, { status: 'REJECTED' }).toString();
    return await get<BackendWorkflow[]>(`/api/v1/workflows?${qs}`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export async function getQuarantinedWorkflows(filters?: WorkflowListFilters): Promise<BackendWorkflow[]> {
  try {
    const qs = buildWorkflowParams(filters, { status: 'QUARANTINED' }).toString();
    return await get<BackendWorkflow[]>(`/api/v1/workflows?${qs}`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

/**
 * Archive's Quarantine and Deleted (rejected) tabs were merged into one
 * "Rejected" tab (W17 D07) — both cover the same operator need ("someone
 * made a call to pull this booking out of the pipeline, give them a way
 * to bring it back"), and workflow-service's reingest endpoint now
 * accepts REJECTED alongside QUARANTINED (see
 * [[finding-rejected-quarantine-tabs-merged]]). GET /api/v1/workflows
 * only filters on a single status, so this fetches both in parallel and
 * merges them client-side rather than adding multi-status filtering
 * server-side for what is otherwise a two-value union.
 */
export async function getRejectedArchive(filters?: WorkflowListFilters): Promise<BackendWorkflow[]> {
  // GET /api/v1/workflows actually returns a paginated { items, total }
  // shape at runtime despite the Promise<BackendWorkflow[]> type on
  // getDeletedArchive/getQuarantinedWorkflows below — every other caller
  // (SentList, the old DeletedList/QuarantineList) unwraps this
  // defensively before use. Spreading the raw response directly here
  // threw "... is not iterable" once it hit a real paginated payload.
  const unwrap = (data: unknown): BackendWorkflow[] =>
    Array.isArray(data) ? data : ((data as { items?: BackendWorkflow[] })?.items ?? []);

  const [rejected, quarantined] = await Promise.all([
    getDeletedArchive(filters).then(unwrap),
    getQuarantinedWorkflows(filters).then(unwrap),
  ]);
  return [...rejected, ...quarantined].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

export async function reingestWorkflow(workflowId: string): Promise<BackendWorkflow> {
  try {
    return await post<BackendWorkflow>(`/api/v1/workflows/${workflowId}/reingest/user`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export async function retryWorkflow(id: string): Promise<void> {
  try {
    await post(`/api/v1/workflows/${id}/retry`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

// The real backend action behind the InboxQueue's "Dismiss" button on a
// FAILED/stalled/timed-out card. Before this existed, "Dismiss" only ever
// set local component state in InboxQueue (see handleDismiss in
// DashboardPage.tsx) — nothing was ever persisted server-side, so a
// dismissed card reappeared on the very next 30s queue poll or page
// reload, because the workflow was still genuinely RECEIVED/PROCESSING/
// FAILED on the backend the whole time. Reported live 07 Sep 2026
// ("I rejected those emails and they're still in the dashboard" / "the
// glitch is happening again"). This calls the new cookie-auth
// /dismiss endpoint, which transitions FAILED (or stalled PROCESSING)
// workflows to QUARANTINED — a real, valid state-machine edge — so the
// booking actually leaves the active queue for good and shows up in the
// Archive's Quarantine tab, where re-ingest already exists.
export async function dismissWorkflow(id: string): Promise<BackendWorkflow> {
  try {
    return await post<BackendWorkflow>(`/api/v1/workflows/${id}/dismiss`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

export async function fetchAiUsage(draftId: string): Promise<AiUsageData> {
  try {
    return await get<AiUsageData>(`/api/v1/drafts/${draftId}/ai-usage`);
  } catch (err: unknown) {
    throw normaliseError(err);
  }
}

// ---- Legacy helpers (keep for backward compat with existing callers) -----

export async function listBookings(): Promise<BackendJob[]> {
  return getWorkflows();
}

export async function getBooking(id: string): Promise<BookingJob | null> {
  try {
    return (await getWorkflow(id)) as unknown as BookingJob;
  } catch (err: unknown) {
    if (isApiError(err) && err.status === 404) return null;
    throw err;
  }
}

export async function getActive(): Promise<BookingJob> {
  return Promise.resolve(getActiveBooking());
}

export async function listAudit(): Promise<AuditEntry[]> {
  return Promise.resolve(MOCK_AUDIT);
}

export async function listSent(): Promise<BackendWorkflow[]> {
  return getSentArchive();
}

export async function listDeleted(): Promise<BackendWorkflow[]> {
  return getDeletedArchive();
}

// ─── Internal helpers ────────────────────────────────────────────────────────

function normaliseError(err: unknown): IApiError {
  if (isApiError(err)) return err;
  if (err instanceof Error) {
    return { status: 0, message: err.message };
  }
  return { status: 0, message: 'An unexpected error occurred' };
}

function isApiError(err: unknown): err is IApiError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'status' in err &&
    'message' in err
  );
}
